(() => {
  'use strict';

  const normalize = (value = '') => String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const optionKind = (name = '') => {
    const n = normalize(name);
    if (/\b(couleur|color|colour|coloris|teinte|finition)\b/.test(n)) return 'color';
    if (/\b(taille|size)\b/.test(n)) return 'size';
    if (/\b(longueur|length)\b/.test(n)) return 'length';
    if (/\b(puissance|power|ressort|spring)\b/.test(n)) return 'power';
    if (/\b(grammage|poids|weight)\b/.test(n)) return 'weight';
    if (/\b(connecteur|connector|prise)\b/.test(n)) return 'connector';
    if (/\b(tension|voltage)\b/.test(n)) return 'voltage';
    if (/\b(capacite|capacity)\b/.test(n)) return 'capacity';
    if (/\b(version|modele|model)\b/.test(n)) return 'version';
    return 'generic';
  };

  const colorValue = (value = '') => {
    const n = ` ${normalize(value)} `;
    const colors = [
      [/\b(noir|black|bk)\b/, '#111111'],
      [/\b(blanc|white)\b/, '#f5f5f5'],
      [/\b(gris|grey|gray)\b/, '#808080'],
      [/\b(tan|coyote|desert|de)\b/, '#b58b5a'],
      [/\b(od|olive|vert|green)\b/, '#596b3b'],
      [/\b(rouge|red)\b/, '#b52222'],
      [/\b(bleu|blue)\b/, '#275b9f'],
      [/\b(jaune|yellow)\b/, '#d4ad24'],
      [/\b(orange)\b/, '#c96d26'],
      [/\b(violet|purple)\b/, '#76529b'],
      [/\b(rose|pink)\b/, '#c86d8c'],
      [/\b(argent|silver)\b/, '#a7a7a7'],
      [/\b(or|gold)\b/, '#b8953e']
    ];
    for (const [pattern, color] of colors) if (pattern.test(n)) return color;
    return '';
  };

  const bindSwatches = (field) => {
    if (!field || field.dataset.awVariantPickerReady === 'true') return;
    const select = field.querySelector('[data-variant-select]');
    const picker = field.querySelector('[data-aw-variant-picker]');
    const configNode = picker?.querySelector('[data-aw-variant-config]');
    if (!select || !picker || !configNode) return;

    let config;
    try { config = JSON.parse(configNode.textContent || '{}'); } catch (_) { return; }

    const optionNames = Array.isArray(config.options)
      ? config.options.map((name) => String(name || 'Option'))
      : [];
    const variants = Array.isArray(config.variants)
      ? config.variants.map((variant) => ({
          id: String(variant.id || ''),
          available: variant.available === true,
          options: Array.isArray(variant.options) ? variant.options.map((value) => String(value)) : [],
          mediaPreview: typeof variant.mediaPreview === 'string' ? variant.mediaPreview : ''
        })).filter((variant) => variant.id)
      : [];
    if (!optionNames.length || !variants.length) return;

    const groups = [...picker.querySelectorAll('[data-aw-variant-group]')].map((group) => {
      const index = Number(group.dataset.optionIndex);
      const name = optionNames[index] || 'Option';
      const kind = optionKind(name);
      group.classList.add(`aw-variant-picker__group--${kind}`);
      return {
        node: group,
        index,
        name,
        kind,
        current: group.querySelector('[data-aw-option-current]'),
        buttons: [...group.querySelectorAll('[data-aw-variant-choice]')]
      };
    }).filter((group) => Number.isInteger(group.index) && group.buttons.length);
    if (!groups.length) return;

    const byId = new Map(variants.map((variant) => [variant.id, variant]));
    let selected = byId.get(String(select.value || '')) ||
      byId.get(String(config.selectedVariantId || '')) ||
      variants[0];
    if (!selected || selected.options.length < optionNames.length) return;
    let selectedValues = selected.options.slice(0, optionNames.length);

    const candidatesForValue = (index, value) =>
      variants.filter((variant) => String(variant.options[index] || '') === String(value));

    const scoreAgainstSelection = (variant, changedIndex) =>
      optionNames.reduce((score, _, index) => {
        if (index === changedIndex) return score;
        return score + (String(variant.options[index] || '') === String(selectedValues[index] || '') ? 1 : 0);
      }, 0);

    const candidateFor = (index, value, availableOnly = false) => {
      const candidates = candidatesForValue(index, value);
      const pool = availableOnly ? candidates.filter((variant) => variant.available) : candidates;
      if (!pool.length) return null;

      const exact = pool.find((variant) => optionNames.every((_, compareIndex) =>
        compareIndex === index ||
        String(variant.options[compareIndex] || '') === String(selectedValues[compareIndex] || '')
      ));
      if (exact) return exact;

      return pool.slice().sort((a, b) => {
        const diff = scoreAgainstSelection(b, index) - scoreAgainstSelection(a, index);
        return diff || (Number(b.available) - Number(a.available));
      })[0];
    };

    const representativePreview = (index, value) => {
      const contextual = variants.find((variant) =>
        variant.mediaPreview &&
        String(variant.options[index] || '') === String(value) &&
        optionNames.every((_, compareIndex) =>
          compareIndex === index ||
          String(variant.options[compareIndex] || '') === String(selectedValues[compareIndex] || '')
        )
      );
      return contextual?.mediaPreview ||
        variants.find((variant) =>
          variant.mediaPreview && String(variant.options[index] || '') === String(value)
        )?.mediaPreview ||
        '';
    };

    const distinctImagesFor = (index) => {
      const byValue = new Map();
      variants.forEach((variant) => {
        const value = String(variant.options[index] || '');
        if (value && variant.mediaPreview && !byValue.has(value)) byValue.set(value, variant.mediaPreview);
      });
      return new Set(byValue.values()).size > 1;
    };

    groups.forEach((group) => {
      group.distinctImages = distinctImagesFor(group.index);
      group.buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.optionValue || '';
          const candidate = candidateFor(group.index, value, true);
          if (!candidate) return;
          selectedValues = candidate.options.slice(0, optionNames.length);
          select.value = candidate.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          refresh();
        });
      });
    });

    const refreshVisual = (group, button, value) => {
      const visual = button.querySelector('[data-aw-variant-visual]');
      const image = button.querySelector('[data-aw-variant-visual-image]');
      const fallback = button.querySelector('[data-aw-variant-visual-fallback]');
      if (!visual || !image || !fallback) return;

      visual.hidden = true;
      image.hidden = true;
      fallback.hidden = true;
      visual.classList.remove('is-color', 'is-pattern');
      visual.style.removeProperty('--aw-swatch-color');

      if (group.kind !== 'color') return;

      const color = colorValue(value);
      const preview = representativePreview(group.index, value);
      const useImage = group.distinctImages || (!color && preview);

      if (useImage && preview) {
        image.src = preview;
        image.hidden = false;
        visual.hidden = false;
        return;
      }

      if (color) {
        visual.style.setProperty('--aw-swatch-color', color);
        visual.classList.add('is-color');
        visual.hidden = false;
        return;
      }

      visual.classList.add('is-pattern');
      fallback.textContent = String(value).slice(0, 2).toUpperCase();
      fallback.hidden = false;
      visual.hidden = false;
    };

    const refresh = () => {
      const selectedRecord = byId.get(String(select.value || ''));
      if (selectedRecord) {
        selected = selectedRecord;
        selectedValues = selectedRecord.options.slice(0, optionNames.length);
      }

      groups.forEach((group) => {
        const currentValue = String(selectedValues[group.index] || '');
        if (group.current) group.current.textContent = currentValue;

        group.buttons.forEach((button) => {
          const value = button.dataset.optionValue || '';
          const available = candidatesForValue(group.index, value).some((variant) => variant.available);
          const active = value === currentValue;

          button.classList.toggle('is-selected', active);
          button.classList.toggle('is-unavailable', !available);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
          button.setAttribute('aria-label', `${group.name} : ${value}. ${available ? 'Disponible' : 'Épuisé'}.`);
          button.disabled = !available;
          button.setAttribute('aria-disabled', available ? 'false' : 'true');
          refreshVisual(group, button, value);
        });
      });
    };

    select.addEventListener('change', refresh);
    const nativeLabel = field.querySelector(':scope > label');
    if (nativeLabel) nativeLabel.setAttribute('aria-hidden', 'true');
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    field.classList.add('is-aw-variant-enhanced');
    field.dataset.awVariantPickerReady = 'true';
    picker.hidden = false;
    refresh();
  };

  let logisticsRows;
  const readLogistics = () => {
    if (logisticsRows) return logisticsRows;
    const node = document.querySelector('[data-aw-logistics-variants]');
    if (!node) return [];
    try { logisticsRows = JSON.parse(node.textContent || '[]'); }
    catch (_) { logisticsRows = []; }
    return logisticsRows;
  };

  const selectedVariantId = (root) => {
    const select = root.querySelector('[data-variant-select]');
    if (select?.value) return String(select.value);
    const hidden = root.querySelector('.product-form input[name="id"]');
    return hidden?.value ? String(hidden.value) : '';
  };

  const applyLogistics = (root) => {
    if (!root) return;
    const variantId = selectedVariantId(root);
    if (!variantId) return;
    const row = readLogistics().find((item) => String(item.id) === variantId);
    if (!row) return;

    const availability = root.querySelector('[data-aw-availability]');
    if (availability) {
      availability.textContent = row.label || (row.available ? 'Disponible' : 'Indisponible');
      availability.dataset.logisticsState = row.state || '';
      availability.classList.toggle('is-unavailable', !row.available);
    }

    const detail = root.querySelector('[data-aw-logistics-delay]');
    if (detail) {
      const delay = String(row.delay || '').trim();
      detail.textContent = delay;
      detail.hidden = !delay;
    }
  };

  const bindLogistics = (root) => {
    if (!root || root.dataset.awLogisticsReady === 'true') return;
    root.dataset.awLogisticsReady = 'true';
    const select = root.querySelector('[data-variant-select]');
    select?.addEventListener('change', () => queueMicrotask(() => applyLogistics(root)));
    requestAnimationFrame(() => applyLogistics(root));
  };

  const setReviewOpen = (submission, button, label, open) => {
    submission.hidden = !open;
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    label.textContent = open ? 'Masquer le formulaire' : 'Écrire un avis';
  };

  const bindReviews = (reviews) => {
    if (!reviews || reviews.dataset.awReviewCompact === 'true') return;
    const submission = reviews.querySelector('.aw-product-reviews__submission');
    const card = reviews.querySelector('.aw-product-reviews__card');
    const template = document.querySelector('[data-aw-review-compact-template]');
    if (!submission || !card || !template?.content?.firstElementChild) return;

    if (!submission.id) submission.id = `AwReviewSubmission-${Math.random().toString(36).slice(2, 9)}`;
    const action = template.content.firstElementChild.cloneNode(true);
    const button = action.querySelector('[data-aw-review-toggle]');
    const label = action.querySelector('[data-aw-review-toggle-label]');
    if (!button || !label) return;

    button.setAttribute('aria-controls', submission.id);
    card.insertBefore(action, submission);
    const hasFeedback = !!submission.querySelector('.aw-product-reviews__form-success,.aw-product-reviews__form-errors,[role="alert"]');
    reviews.dataset.awReviewCompact = 'true';
    setReviewOpen(submission, button, label, hasFeedback);

    button.addEventListener('click', () => {
      const open = submission.hidden;
      setReviewOpen(submission, button, label, open);
      if (!open) return;
      window.requestAnimationFrame(() => {
        const first = submission.querySelector('input:not([type="hidden"]):not([disabled]),textarea:not([disabled]),select:not([disabled])');
        if (!first) return;
        try { first.focus({ preventScroll: true }); } catch (_) { first.focus(); }
      });
    });
  };

  const bindReviewJump = (button) => {
    if (!button || button.dataset.awReviewJumpReady === 'true') return;
    button.dataset.awReviewJumpReady = 'true';
    button.addEventListener('click', () => {
      const reviews = document.querySelector('.aw-product-reviews');
      if (!reviews) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      reviews.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('.aw-pdp-variant-field').forEach(bindSwatches);
    if (scope.matches?.('.aw-pdp-variant-field')) bindSwatches(scope);
    scope.querySelectorAll?.('[data-product-root]').forEach(bindLogistics);
    if (scope.matches?.('[data-product-root]')) bindLogistics(scope);
    scope.querySelectorAll?.('.aw-product-reviews').forEach(bindReviews);
    if (scope.matches?.('.aw-product-reviews')) bindReviews(scope);
    scope.querySelectorAll?.('[data-aw-scroll-reviews]').forEach(bindReviewJump);
    if (scope.matches?.('[data-aw-scroll-reviews]')) bindReviewJump(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => {
    logisticsRows = undefined;
    initAll(event.target);
  });
})();

(() => {
  'use strict';

  const hasServerApprovedCards = (section) => Boolean(
    section.querySelector('[data-aw-recommendation-card]')
  );

  const loadRecommendations = async (section) => {
    if (!section?.dataset.url || section.dataset.loaded === 'true') return;
    section.dataset.loaded = 'true';

    try {
      const response = await fetch(section.dataset.url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const wrapper = document.createElement('div');
      wrapper.innerHTML = await response.text();
      const replacement = wrapper.querySelector(`#${CSS.escape(section.id)}`);

      if (!replacement || !replacement.innerHTML.trim() || !hasServerApprovedCards(replacement)) {
        section.remove();
        return;
      }

      section.innerHTML = replacement.innerHTML;
    } catch (_) {
      section.remove();
    }
  };

  const initRecommendations = (root = document) => {
    if (root.matches?.('[data-product-recommendations]')) loadRecommendations(root);
    root.querySelectorAll?.('[data-product-recommendations]').forEach((section) => loadRecommendations(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initRecommendations(), { once: true });
  } else {
    initRecommendations();
  }

  document.addEventListener('shopify:section:load', (event) => initRecommendations(event.target));
})();
